/**
 * Service Flow v2 feature configuration.
 *
 * The v2 experience is enabled purely by the organization's server-stamped
 * capability (Organizations.serviceFlowSchemaVersion = 2, set by the v2
 * backfill / default for new orgs). There is no client release flag — the
 * server RPCs remain the authority for permissions and capability.
 *
 * Every other combination gets the read-only / upgrading experience — never a
 * legacy write. Unknown/missing capability must NOT fall back to legacy
 * writes (spec §15.1).
 */
export const isServiceFlowV2Available = (organization) =>
    organization?.serviceFlowSchemaVersion === 2;

/**
 * Flow item types (spec §4). Stored discriminants — do not localize or rename.
 */
export const SERVICE_FLOW_ITEM_TYPES = {
    AGENDA: {
        type: 'AGENDA',
        label: 'Agenda',
        icon: 'list-outline',
        description: 'A part of the service — prayer, message, announcements.',
    },
    TASK: {
        type: 'TASK',
        label: 'Task',
        icon: 'clipboard-outline',
        description: 'A real task people can be assigned to and respond to.',
    },
    WORSHIP: {
        type: 'WORSHIP',
        label: 'Worship set',
        icon: 'musical-notes-outline',
        description: 'A worship set. Songs and positions are coming later.',
        // Kept in the contract for Part 2; not offered in the type picker yet.
        hiddenInPicker: true,
    },
};

export const SERVICE_FLOW_ITEM_TYPE_LIST = [
    SERVICE_FLOW_ITEM_TYPES.AGENDA,
    SERVICE_FLOW_ITEM_TYPES.TASK,
    SERVICE_FLOW_ITEM_TYPES.WORSHIP,
];

/**
 * Documented input limits (spec §4.1, §10.1). Client-side sanity bounds only —
 * the server remains authoritative.
 */
export const SERVICE_FLOW_LIMITS = {
    maxItems: 200,
    titleMinLength: 1,
    titleMaxLength: 255,
    notesMaxLength: 10000,
    durationMinMinutes: 1,
    durationMaxMinutes: 1440,
    offsetMinMinutes: -10080,
    offsetMaxMinutes: 10080,
    /** Template library pagination (spec §3.2). */
    libraryPageSize: 50,
    /** Standard react-query staleness for v2 flow reads (spec §12.5). */
    staleTimeMs: 30 * 1000,
};

/**
 * Only Tasks with this dueType may back a TASK flow item (spec §4.3).
 */
export const SERVICE_FLOW_TASK_DUE_TYPE = 'EVENT_SERVICE_FLOW';

/**
 * Stable machine-readable error detail codes from the v2 RPCs (spec §10.4).
 */
export const SERVICE_FLOW_ERROR_CODES = {
    conflict: 'SERVICE_FLOW_CONFLICT',
    taskInUse: 'SERVICE_FLOW_TASK_IN_USE',
    templateArchived: 'SERVICE_FLOW_TEMPLATE_ARCHIVED',
    upgradeRequired: 'SERVICE_FLOW_UPGRADE_REQUIRED',
    sharedScopeDenied: 'TEMPLATE_SHARED_SCOPE_DENIED',
};
